/**
 * Story 10.19 (D3) — THE ONE VOCABULARY for a browser failure report.
 *
 * The contract between three things that ship on different schedules: the
 * client bundle that sends a report (`clientFailureReport.js`), the server
 * route that accepts it (`server/api/client-failure.js`), and the Sentry alert
 * rule that matches the event the server raises. A seller's open tab can run a
 * bundle older or newer than the server, so the server never trusts a string
 * from the body: every value is mapped through these lists, and anything else
 * becomes `unrecognised` (which keeps a version skew visible instead of
 * dropping it).
 *
 * Authored as CommonJS so the SAME lists are loaded by the webpack client
 * bundle (`import`) and the plain-Node server (`require`) — `yarn start` runs
 * `node server/index.js` directly and cannot load ESM from `src/`. Precedent:
 * `src/util/listingAvailability.js`, consumed by `server/api/download-ad.js`.
 *
 * Every list is pinned LITERALLY in `clientFailureVocabulary.test.js`
 * (iterating an allowlist is blind to removal).
 */

// 🔴 The Sentry rule for #tph-system-health filters on `alert_type` EQUALS this
// value. Renaming it makes the rule stop matching, silently. See the tag in
// `server/log.js` `clientFailure`.
const ALERT_TYPE = 'client_failure';

// D9 — ONE issue for every report, so a per-issue "3 events in 15 minutes"
// rule counts every failure together. Never interpolate anything into it.
const FINGERPRINT = Object.freeze(['client-failure']);

const ROUTE = '/api/client-failure';

const REPORT_VERSION = 1;

const UNRECOGNISED = 'unrecognised';

const SURFACES = Object.freeze(['my_listings', 'edit_listing', 'checkout_handoff']);

const ACTIONS = Object.freeze([
  'listing_hold',
  'listing_reactivate',
  'listing_mark_sold',
  'listing_cancel',
  'tier_fetch',
  'tier_upgrade_preview',
  'tier_upgrade',
]);

// code -> kind. `kind` is derived on the server, never read from the body; a
// `rejected` with a 5xx status is `transport` (kindOf, below — cold review F1).
const CODE_KIND = Object.freeze({
  tph_api_unreachable: 'transport',
  tph_api_5xx: 'transport',
  tph_api_unexpected_status: 'transport',
  tph_api_unreadable_body: 'transport',
  tph_api_unavailable: 'transport',
  not_authenticated: 'auth',
  token_rejected: 'auth',
  rejected: 'rejected',
  unrecognised: 'other',
});

const CODES = Object.keys(CODE_KIND);

const KINDS = Object.freeze(['transport', 'auth', 'rejected', 'other']);

const fromList = (list, value) =>
  typeof value === 'string' && list.includes(value) ? value : UNRECOGNISED;

/**
 * An HTTP status is kept only when it is an integer from 100 to 599.
 * @returns {number|undefined}
 */
const safeHttpStatus = value =>
  Number.isInteger(value) && value >= 100 && value <= 599 ? value : undefined;

/**
 * Cold review 2026-10-12 F1 — operator ruling: ANY 5xx from tph-api pages
 * Slack, whatever its type; only typed 4xx refusals stay out of the Slack rule
 * (D13a, whose filter is `client_failure_kind` is not `rejected`). A typed 500
 * (`config_fault`) or 502 (`collect_failed`) reaches the client as an error
 * with a code, so it classifies as `rejected`; its STATUS is what says it is
 * our failure. So a `rejected` report with a valid 5xx status gets the kind
 * `transport`. (Every other code already pages: `transport`, `auth` and `other`
 * are all outside the rule's filter.) Decided here, on the server, from the
 * report's code and status — so a seller's open tab running an older bundle is
 * classified the same way.
 *
 * @param {string} code a vocabulary code (already normalised)
 * @param {number|undefined} httpStatus a status from safeHttpStatus
 * @returns {string} one of KINDS
 */
const kindOf = (code, httpStatus) =>
  code === 'rejected' && httpStatus !== undefined && httpStatus >= 500
    ? 'transport'
    : CODE_KIND[code];

/**
 * Map a report onto the tag set. Reads exactly four fields and nothing else.
 *
 * @param {Object} report
 * @returns {Object} `client_failure_surface`, `_action`, `_code`, `_kind`, and
 *   `client_failure_http_status` only when the status is a valid HTTP status.
 */
const normalise = ({ surface, action, code, status } = {}) => {
  const safeCode = fromList(CODES, code);
  const httpStatus = safeHttpStatus(status);
  const tags = {
    client_failure_surface: fromList(SURFACES, surface),
    client_failure_action: fromList(ACTIONS, action),
    client_failure_code: safeCode,
    client_failure_kind: kindOf(safeCode, httpStatus),
  };
  if (httpStatus !== undefined) {
    tags.client_failure_http_status = httpStatus;
  }
  return tags;
};

module.exports = {
  ALERT_TYPE,
  FINGERPRINT,
  ROUTE,
  REPORT_VERSION,
  UNRECOGNISED,
  SURFACES,
  ACTIONS,
  CODE_KIND,
  CODES,
  KINDS,
  safeHttpStatus,
  kindOf,
  normalise,
};
